import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { Modal } from '../../../components/ui/Modal.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { usersApi } from '../../../api/users.js';
import { useMfaStepUp } from '../../../hooks/useMfaStepUp.js';
import { MfaPrompt } from '../../../components/MfaPrompt.jsx';
import { ApiError } from '../../../api/client.js';
import { applyServerValidation } from '../../../lib/forms.js';
import { t } from '../../../lib/i18n.js';

export function PasswordResetModal({ open, onClose, user }) {
  const { run, mfaProps } = useMfaStepUp();
  const { register, handleSubmit, reset, setError, formState: { errors } } = useForm({ defaultValues: { password: '' } });

  useEffect(() => { if (open) reset({ password: '' }); }, [open, reset]);

  const mutation = useMutation({
    mutationFn: (values) => run(() => usersApi.resetPassword(user._id, { password: values.password })),
    onSuccess: () => {
      toast.success('Password aggiornata.');
      onClose();
    },
    onError: (err) => {
      const applied = applyServerValidation(err, setError);
      if (!applied) toast.error(err instanceof ApiError ? err.message : t('common.error'));
    }
  });

  return (
    <>
      <Modal
        open={open}
        onClose={mutation.isPending ? undefined : onClose}
        title="Reimposta password"
        description={user ? `Utente ${user.email}` : ''}
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>{t('common.cancel')}</Button>
            <Button form="password-reset" type="submit" loading={mutation.isPending} variant="danger">Reimposta</Button>
          </>
        }
      >
        <form id="password-reset" className="flex flex-col gap-3" onSubmit={handleSubmit(mutation.mutate)} noValidate>
          <Input
            label="Nuova password"
            type="password"
            hint="min 12 caratteri, maiuscole, minuscole e cifre"
            error={errors.password?.message}
            {...register('password', {
              required: 'Password richiesta',
              minLength: { value: 12, message: 'almeno 12 caratteri' },
              validate: (v) => (
                /[a-z]/.test(v) && /[A-Z]/.test(v) && /\d/.test(v) ||
                'serve maiuscola, minuscola e una cifra'
              )
            })}
          />
          <p className="text-xs text-ink-muted">L'operazione richiede una verifica MFA recente.</p>
        </form>
      </Modal>
      <MfaPrompt {...mfaProps} />
    </>
  );
}
